import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import backArrowIcon from '../../assets/arrow-down.svg'
import closeIcon from '../../assets/close.svg'
import UploadImageIcon from '../../assets/uploadImageIcon.png'
import { APPID } from '../../constants/ApiEndpoints'
import { useIsMobile } from '../../hooks/useIsMobile'
import { usePostAIDesign } from '../../hooks/usePostAIDesign'
import { FEED_RESET } from '../../redux/constants'
import { capture } from '../../redux/designerReducers/canvasReducer'
import { fetchFeed, setAttr } from '../../redux/feedInfiniteSlice'
import { getUserToken } from '../../utils/fetchUtils'
import { getMembershipType } from '../../utils/getMembershipType'

// Room type icons
import logo from '../../assets/dm-mobile-white.svg'
import file from '../../assets/local-file.svg'
import backyardIcon from '../../assets/room-types/backyard.svg'
import balconyIcon from '../../assets/room-types/balcony.svg'
import bathroomIcon from '../../assets/room-types/bathroom.svg'
import bedroomIcon from '../../assets/room-types/beds.svg'
import diningIcon from '../../assets/room-types/dining.svg'
import doorIcon from '../../assets/room-types/door.svg'
import entranceIcon from '../../assets/room-types/entrance.svg'
import playroomIcon from '../../assets/room-types/games.svg'
import kidsIcon from '../../assets/room-types/kids.svg'
import livingIcon from '../../assets/room-types/living.svg'
import officeIcon from '../../assets/room-types/office.svg'
import {
  AIDesignBody,
  AIIcon,
  BackArrowWrapper,
  BackButtonWrapper,
  CoinIcon,
  ColorContainer,
  ColorName,
  ColorOption,
  ColorPaletteGrid,
  ColorSwatch,
  Content,
  Credits,
  CreditsDisplay,
  CustomStyleIcon,
  ErrorContainer,
  ErrorText,
  FormWrapper,
  GenerateButton,
  Header,
  HeaderContent,
  ImagePlaceholderInner,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  ModalCloseButton,
  ModalContainer,
  ModalLabel,
  ModalOverlay,
  ModalSaveButton,
  ModalTextarea,
  ModalTitle,
  PostButton,
  RegenerateButton,
  RegenerateButtonText,
  RegenerateTitle,
  ReplaceImage,
  ResultBottomSection,
  ResultButtonsSection,
  ResultImage,
  ResultImageSection,
  ResultModalCloseButton,
  ResultModalContainer,
  ResultModalHeader,
  ResultModalTitle,
  RetryButton,
  RoomImageModalButton,
  RoomImageModalButtonIcon,
  RoomImageModalButtons,
  RoomImageModalContainer,
  RoomImageModalDescription,
  RoomImageModalTitle,
  RoomImagePlaceholder,
  RoomImageSection,
  RoomTypeChip,
  RoomTypeGrid,
  SectionHeader,
  SectionTitle,
  StyleContainer,
  StyleGrid,
  StyleImage,
  StyleName,
  StyleOption,
  StylePreviewCard,
  StylePreviewImage,
  StylePreviewName,
  StylesPreviewRow,
  StyleViewAll,
  WelcomeAvatar,
  WelcomeSection,
  WelcomeText
} from './CreateAIDesign.styles'

// Import ChooseTemplate component
import { ChooseTemplate } from '../CreateUserEvent/components/ChooseTemplate'

// Import AllStylesModal component
import { AllStylesModal } from './AllStylesModal'

// Import PostFilters component
import { PostFilters } from '../PostModal/PostFilters'

// AI designer style images
import artdecoImg from '../../assets/ai-designer/Artdeco.png'
import baroqueImg from '../../assets/ai-designer/Baroque.png'
import bohemianImg from '../../assets/ai-designer/Bohemian.png'
import brightOldMoneyImg from '../../assets/ai-designer/Brightoldmoney.png'
import coastalImg from '../../assets/ai-designer/Coastal.png'
import contemporaryImg from '../../assets/ai-designer/Contemporary.png'
import esportImg from '../../assets/ai-designer/Esport.png'
import farmhouseImg from '../../assets/ai-designer/Farmhouse.png'
import industrialImg from '../../assets/ai-designer/Industrial.png'
import japandiImg from '../../assets/ai-designer/Japandi.png'
import luxuryImg from '../../assets/ai-designer/Luxury.png'
import mediterraneanImg from '../../assets/ai-designer/Mediterranean.png'
import midcenturyImg from '../../assets/ai-designer/Midcentury.png'
import modernImg from '../../assets/ai-designer/Modern.png'
import scandinavianImg from '../../assets/ai-designer/Scandinavian.png'
import sohoImg from '../../assets/ai-designer/Soho.png'
import traditionalImg from '../../assets/ai-designer/Traditional.png'
import tropicalImg from '../../assets/ai-designer/Tropical.png'
import urbanImg from '../../assets/ai-designer/Urban.png'
import wabisabiImg from '../../assets/ai-designer/Wabisabi.png'
import bambooTexture from '../../assets/ai-designer/bamboo.png'
import goldTexture from '../../assets/ai-designer/gold.png'
import silverTexture from '../../assets/ai-designer/silver.png'
import walnutTexture from '../../assets/ai-designer/walnut.png'
import coinIcon from '../../assets/coin.svg'
import dmIconCircle from '../../assets/dmIconCircle.png'
import { purchaseAIDesign } from '../../redux/designerReducers/cartReducer'
import BackButton from '../navs/BackButton'

// Style options data
const designStyles = [
  { id: 'custom', name: 'Custom', image: null, isCustom: true },
  { id: 'mediterranean', name: 'Mediterranean', image: mediterraneanImg },
  { id: 'modern', name: 'Modern', image: modernImg },
  { id: 'japandi', name: 'Japandi', image: japandiImg },
  { id: 'wabi-sabi', name: 'Wabi-Sabi', image: wabisabiImg },
  { id: 'mid-century', name: 'Mid-Century', image: midcenturyImg },
  { id: 'art-deco', name: 'Art Deco', image: artdecoImg },
  { id: 'baroque', name: 'Baroque', image: baroqueImg },
  { id: 'bohemian', name: 'Bohemian', image: bohemianImg },
  { id: 'bright-old-money', name: 'Bright Old Money', image: brightOldMoneyImg },
  { id: 'coastal', name: 'Coastal', image: coastalImg },
  { id: 'contemporary', name: 'Contemporary', image: contemporaryImg },
  { id: 'esport', name: 'Esport', image: esportImg },
  { id: 'farmhouse', name: 'Farmhouse', image: farmhouseImg },
  { id: 'industrial', name: 'Industrial', image: industrialImg },
  { id: 'luxury', name: 'Luxury', image: luxuryImg },
  { id: 'scandinavian', name: 'Scandinavian', image: scandinavianImg },
  { id: 'soho', name: 'Soho', image: sohoImg },
  { id: 'traditional', name: 'Traditional', image: traditionalImg },
  { id: 'tropical', name: 'Tropical', image: tropicalImg },
  { id: 'urban', name: 'Urban', image: urbanImg }
]

// Room types data with icons
const roomTypes = [
  { id: 'living-room', name: 'Living Room', icon: livingIcon },
  { id: 'bedroom', name: 'Bedroom', icon: bedroomIcon },
  { id: 'bathroom', name: 'Bathroom', icon: bathroomIcon },
  { id: 'dining', name: 'Dining', icon: diningIcon },
  { id: 'kitchen', name: 'Kitchen', icon: diningIcon }, // Using dining icon for kitchen
  { id: 'kids', name: 'Kids', icon: kidsIcon },
  { id: 'nursery', name: 'Nursery', icon: kidsIcon }, // Using kids icon for nursery
  { id: 'home-office', name: 'Home Office', icon: officeIcon },
  { id: 'playroom', name: 'Playroom', icon: playroomIcon },
  { id: 'backyard', name: 'Backyard', icon: backyardIcon },
  { id: 'balcony', name: 'Balcony', icon: balconyIcon },
  { id: 'rooftop', name: 'Rooftop', icon: balconyIcon }, // Using balcony icon for rooftop
  { id: 'front-door', name: 'Front Door', icon: doorIcon },
  { id: 'entrance', name: 'Entrance', icon: entranceIcon }
]

// Color palette data
const colorPalette = [
  { id: 'beige', name: 'Beige', color: '#FAEDCD' },
  { id: 'white', name: 'White', color: '#FFFFFF', border: true },
  { id: 'bamboo', name: 'Bamboo', texture: bambooTexture },
  { id: 'walnut', name: 'Walnut', texture: walnutTexture },
  { id: 'black', name: 'Black', color: '#000000' },
  { id: 'pink', name: 'Pink', color: '#E69D96' },
  { id: 'red', name: 'Red', color: '#EA5B5B' },
  { id: 'green', name: 'Green', color: '#8EAE70' },
  { id: 'blue', name: 'Blue', color: '#76A2C7' },
  { id: 'yellow', name: 'Yellow', color: '#EAC96F' },
  { id: 'orange', name: 'Orange', color: '#E9A77B' },
  { id: 'purple', name: 'Purple', color: '#B190BD' },
  { id: 'gray', name: 'Gray', color: '#6B6B6B' },
  { id: 'silver', name: 'Silver', texture: silverTexture },
  { id: 'gold', name: 'Gold', texture: goldTexture }
]

export const CreateAIDesign = () => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: {
      roomImage: null,
      selectedStyle: 'mediterranean',
      selectedRoomType: 'living-room',
      selectedColors: [],
      customStyleDescription: ''
    }
  })

  const router = useRouter()
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const [showImageModal, setShowImageModal] = useState(false)
  const [showCustomModal, setShowCustomModal] = useState(false)
  const [showRoomImageModal, setShowRoomImageModal] = useState(false)
  const [showChooseTemplate, setShowChooseTemplate] = useState(false)
  const [customStyleText, setCustomStyleText] = useState('')
  const [showAllStylesModal, setShowAllStylesModal] = useState(false)
  const [selectedStyleInModal, setSelectedStyleInModal] = useState('')
  const [showResultModal, setShowResultModal] = useState(false)
  const [resultImage, setResultImage] = useState(null)
  const [selectedRegenerateStyle, setSelectedRegenerateStyle] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationError, setGenerationError] = useState(null)
  const [showPostFilters, setShowPostFilters] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState(null)
  const [isSending, setIsSending] = useState(false)

  // Get profile data for membership checks
  const profile = useSelector(state => state.profile.me)
  const membershipInfo = getMembershipType(profile)

  // Use the AI design posting hook
  const postAIDesignHook = usePostAIDesign({
    setIsSending,
    onClose: () => setShowPostFilters(false),
    member: membershipInfo.isMember,
    postText: '', // Could be enhanced to include AI description
    selectedFilter,
    aiImageUrl: resultImage
  })

  const watchRoomImage = watch('roomImage')
  const watchStyle = watch('selectedStyle')
  const watchRoomType = watch('selectedRoomType')
  const watchColors = watch('selectedColors')

  useEffect(() => {
    document.querySelector('#scroll-box')?.scrollTo(0, 0)
  }, [])

  // Fetch filters when component mounts
  useEffect(() => {
    const fetchFilters = async () => {
      const attr = {
        addition: {
          categoryIds: ['animation']
        }
      }

      dispatch(setAttr('itemFeed')(attr))
      await dispatch(fetchFeed('itemFeed')({ status: FEED_RESET }))
      await dispatch(fetchFeed('itemFeed')({ status: null }))
    }

    fetchFilters()
  }, [dispatch])

  useEffect(() => {
    // Pre-fill modal with existing custom text when opening
    if (showCustomModal) {
      const existingText = getValues('customStyleDescription')
      setCustomStyleText(existingText || '')
    }
  }, [showCustomModal, getValues])

  const handleImageUpload = event => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = e => {
        setValue('roomImage', e.target.result)
        setShowRoomImageModal(false)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleStyleSelect = styleId => {
    if (styleId === 'custom') {
      setShowCustomModal(true)
    } else {
      setValue('selectedStyle', styleId)
      setValue('customStyleDescription', '')
      setCustomStyleText('')
    }
  }

  const openAllStylesModal = () => {
    setSelectedStyleInModal(getValues('selectedStyle'))
    setShowAllStylesModal(true)
  }

  const applyStyleSelection = () => {
    const chosen = selectedStyleInModal || getValues('selectedStyle')
    setValue('selectedStyle', chosen)
    if (chosen !== 'custom') {
      setValue('customStyleDescription', '')
      setCustomStyleText('')
    }
    setShowAllStylesModal(false)
    if (chosen === 'custom' && !getValues('customStyleDescription')) {
      setShowCustomModal(true)
    }
  }

  const handleCustomStyleSave = () => {
    if (customStyleText.trim()) {
      setValue('selectedStyle', 'custom')
      setValue('customStyleDescription', customStyleText.trim())
      setShowCustomModal(false)
    }
  }

  const handleCustomModalClose = () => {
    setShowCustomModal(false)
    // If no custom text was saved, reset to a different style
    if (!getValues('customStyleDescription')) {
      setValue('selectedStyle', 'mediterranean')
    }
  }

  const handleTemplateSelect = url => {
    setValue('roomImage', url)
    setShowChooseTemplate(false)
    setShowRoomImageModal(false)
  }

  const getCustomDisplayText = () => {
    const customDesc = getValues('customStyleDescription')
    if (!customDesc) return 'Custom'
    // Truncate text to fit card width (approximately 20 characters)
    return customDesc.length > 20 ? customDesc.substring(0, 20) + '...' : customDesc
  }

  const handleRoomTypeSelect = roomTypeId => {
    setValue('selectedRoomType', roomTypeId)
  }

  const handleColorSelect = colorId => {
    const currentColors = watchColors || []
    const isSelected = currentColors.includes(colorId)

    if (isSelected) {
      setValue(
        'selectedColors',
        currentColors.filter(id => id !== colorId)
      )
    } else {
      setValue('selectedColors', [...currentColors, colorId])
    }
  }

  const onSubmit = async data => {
    // console.log('AI Design Generation data:', data)

    // Validate that room image is provided
    if (!data.roomImage) {
      alert('Please upload a room image to continue')
      return
    }

    // Reset states and show modal immediately
    setGenerationError(null)
    setResultImage(null)
    setIsGenerating(true)
    setSelectedRegenerateStyle(data.selectedStyle)
    setShowResultModal(true)

    try {
      // Get auth token
      const authToken = getUserToken()

      // Purchase AI design on backend (deducts coins on server)
      await dispatch(
        purchaseAIDesign({
          isMember: membershipInfo.isMember,
          cost: 1000
        })
      )

      // Call our API endpoint
      const response = await fetch('/api/ai-design-generation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formData: data,
          authToken: authToken,
          appId: APPID
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate AI design')
      }

      if (result.success && result.imageUrl) {
        setResultImage(result.imageUrl)
        setGenerationError(null)
      } else {
        throw new Error('No image generated')
      }
    } catch (error) {
      console.error('AI Generation Error:', error)
      setGenerationError(error.message || 'Failed to generate design')
      setResultImage(null)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleRegenerateStyleSelect = styleId => {
    setSelectedRegenerateStyle(styleId)
  }

  const handleRegenerate = async () => {
    console.log('Regenerating with style:', selectedRegenerateStyle)

    if (!selectedRegenerateStyle) {
      alert('Please select a style first')
      return
    }

    setGenerationError(null)
    setResultImage(null)
    setIsGenerating(true)

    try {
      const authToken = getUserToken()
      const currentFormData = getValues()

      // Update the style for regeneration
      const regenerateData = {
        ...currentFormData,
        selectedStyle: selectedRegenerateStyle
      }

      // Purchase AI design regeneration on backend (deducts coins on server)
      await dispatch(
        purchaseAIDesign({
          isMember: membershipInfo.isMember,
          cost: 1000
        })
      )

      const response = await fetch('/api/ai-design-generation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formData: regenerateData,
          authToken: authToken,
          appId: APPID
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to regenerate AI design')
      }

      if (result.success && result.imageUrl) {
        setResultImage(result.imageUrl)
        setGenerationError(null)
      } else {
        throw new Error('No image generated')
      }
    } catch (error) {
      console.error('AI Regeneration Error:', error)
      setGenerationError(error.message || 'Failed to regenerate design')
      setResultImage(null)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleRetry = () => {
    const currentFormData = getValues()
    onSubmit(currentFormData)
  }

  const handlePost = () => {
    console.log('Posting AI-generated design')

    // Store the AI-generated image in canvas state for PostFilters to use
    dispatch(capture(resultImage))

    // Close result modal and open post filters
    setShowResultModal(false)
    setShowPostFilters(true)
  }

  const handleCloseResultModal = () => {
    setShowResultModal(false)
    setResultImage(null)
    setSelectedRegenerateStyle('')
    setIsGenerating(false)
    setGenerationError(null)
  }

  const handlePostFiltersClose = () => {
    setShowPostFilters(false)
    setSelectedFilter(null)
  }

  const handlePostFiltersBack = () => {
    setShowPostFilters(false)
    setShowResultModal(true)
  }

  const handleSubmitAIPost = () => {
    postAIDesignHook()
  }

  const HeaderContentElements = () => (
    <>
      <h1>AI Designer</h1>
      <CreditsDisplay>
        <GenerateButton type="submit">
          AI Generate
          <AIIcon>
            <img src={coinIcon} alt="" />
          </AIIcon>
          <Credits>1000</Credits>
        </GenerateButton>
      </CreditsDisplay>
    </>
  )

  return (
    <AIDesignBody>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Content>
          <Header>
            {isMobile && (
              <BackArrowWrapper
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  router.back()
                }}
              >
                <img src={backArrowIcon} alt="" />
              </BackArrowWrapper>
            )}
            {!isMobile && (
              <BackButtonWrapper>
                <BackButton autoPosition={false} />
              </BackButtonWrapper>
            )}
            {isMobile ? (
              <HeaderContentElements />
            ) : (
              <HeaderContent>
                <HeaderContentElements />
              </HeaderContent>
            )}
          </Header>

          <FormWrapper>
            {/* Welcome Message */}
            <WelcomeSection>
              <WelcomeAvatar>
                <img src={dmIconCircle} alt="" />
              </WelcomeAvatar>
              <WelcomeText>Welcome! Let's start your AI design journey with us.</WelcomeText>
            </WelcomeSection>

            {/* Room Image Upload */}
            <SectionTitle>Your Room</SectionTitle>
            <RoomImageSection>
              <RoomImagePlaceholder onClick={() => setShowRoomImageModal(true)} hasImage={!!watchRoomImage} imageUrl={watchRoomImage}>
                {watchRoomImage ? (
                  <ReplaceImage>Replace</ReplaceImage>
                ) : (
                  <ImagePlaceholderInner>
                    <img src={UploadImageIcon} alt="Upload" />
                  </ImagePlaceholderInner>
                )}
              </RoomImagePlaceholder>
            </RoomImageSection>

            {/* Style Selection */}
            <SectionHeader>
              <SectionTitle>Select Style</SectionTitle>
              <StyleViewAll onClick={openAllStylesModal}>
                View All
                <svg width="18" height="9" viewBox="0 0 18 9" fill="currentColor">
                  <path d="M18 4.5L13 0v9l5-4.5zM0 4.5h14v1H0V4.5z" />
                </svg>
              </StyleViewAll>
            </SectionHeader>
            <StyleGrid>
              {designStyles.map(style => (
                <StyleOption key={style.id} selected={watchStyle === style.id} onClick={() => handleStyleSelect(style.id)} isCustom={style.isCustom}>
                  {style.isCustom ? <CustomStyleIcon>🎨</CustomStyleIcon> : <StyleImage src={style.image} alt={style.name} />}
                  <StyleContainer>
                    <StyleName selected={watchStyle === style.id}>{style.isCustom ? getCustomDisplayText() : style.name}</StyleName>
                  </StyleContainer>
                </StyleOption>
              ))}
            </StyleGrid>

            {/* Room Type Selection */}
            <SectionTitle>Select Room</SectionTitle>
            <RoomTypeGrid>
              {roomTypes.map(room => (
                <RoomTypeChip key={room.id} selected={watchRoomType === room.id} onClick={() => handleRoomTypeSelect(room.id)}>
                  <img src={room.icon} alt={room.name} />
                  {room.name}
                </RoomTypeChip>
              ))}
            </RoomTypeGrid>

            {/* Color Selection */}
            <SectionTitle>
              Your Room <span>(optional)</span>
            </SectionTitle>
            <ColorPaletteGrid>
              {colorPalette.map(color => (
                <ColorOption key={color.id} selected={watchColors?.includes(color.id)} onClick={() => handleColorSelect(color.id)}>
                  <ColorName>{color.name}</ColorName>
                  <ColorContainer selected={watchColors?.includes(color.id)}>
                    <ColorSwatch color={color.color} texture={color.texture} border={color.border} selected={watchColors?.includes(color.id)} />
                  </ColorContainer>
                </ColorOption>
              ))}
            </ColorPaletteGrid>

            {!isMobile && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '60px'
                }}
              >
                <GenerateButton type="submit">
                  AI Generate
                  <AIIcon>
                    <img src={coinIcon} alt="" />
                  </AIIcon>
                  <Credits>1000</Credits>
                </GenerateButton>
              </div>
            )}
          </FormWrapper>
        </Content>
      </form>

      {/* Custom Style Modal */}
      {showCustomModal && (
        <ModalOverlay onClick={handleCustomModalClose}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalCloseButton onClick={handleCustomModalClose}>
              <img src={closeIcon} alt="Close" />
            </ModalCloseButton>
            <ModalTitle>Custom</ModalTitle>
            <ModalLabel>Your Description</ModalLabel>
            <ModalTextarea placeholder="Example: Add a natural colored sofa, dark coffee table, abstract wall art, and a fiddle leaf plant in the corner." value={customStyleText} onChange={e => setCustomStyleText(e.target.value)} maxLength={500} />
            <ModalSaveButton onClick={handleCustomStyleSave} disabled={!customStyleText.trim()}>
              Save
            </ModalSaveButton>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Room Image Selection Modal */}
      {showRoomImageModal && (
        <ModalOverlay onClick={() => setShowRoomImageModal(false)}>
          <RoomImageModalContainer onClick={e => e.stopPropagation()}>
            <ModalCloseButton onClick={() => setShowRoomImageModal(false)}>
              <img src={closeIcon} alt="Close" />
            </ModalCloseButton>
            <RoomImageModalTitle>Select Template</RoomImageModalTitle>
            <RoomImageModalDescription>Ready to start your AI design journey? Choose a background to get started:</RoomImageModalDescription>
            <RoomImageModalButtons>
              <RoomImageModalButton onClick={() => setShowChooseTemplate(true)}>
                <RoomImageModalButtonIcon>
                  <img src={logo} alt="Library" />
                </RoomImageModalButtonIcon>
                Our Image Library
              </RoomImageModalButton>
              <RoomImageModalButton as="label" outlined>
                <RoomImageModalButtonIcon outlined>
                  <img src={file} alt="Upload" />
                </RoomImageModalButtonIcon>
                Local Device
                <input type="file" accept="image/*" onChange={handleImageUpload} />
              </RoomImageModalButton>
            </RoomImageModalButtons>
          </RoomImageModalContainer>
        </ModalOverlay>
      )}

      {/* Choose Template Modal */}
      {showChooseTemplate && <ChooseTemplate fieldName="roomImage" onClose={() => setShowChooseTemplate(false)} setValue={handleTemplateSelect} />}

      {/* All Styles Modal */}
      <AllStylesModal isOpen={showAllStylesModal} onClose={() => setShowAllStylesModal(false)} designStyles={designStyles} selectedStyleInModal={selectedStyleInModal} setSelectedStyleInModal={setSelectedStyleInModal} onApply={applyStyleSelection} />

      {/* AI Result Modal */}
      {showResultModal && (
        <ModalOverlay onClick={handleCloseResultModal}>
          <ResultModalContainer onClick={e => e.stopPropagation()}>
            <ResultModalHeader>
              <ResultModalTitle>AI Designer</ResultModalTitle>
              <ResultModalCloseButton onClick={handleCloseResultModal}>
                <img src={closeIcon} alt="Close" />
              </ResultModalCloseButton>
            </ResultModalHeader>

            <ResultImageSection>
              {isGenerating && (
                <LoadingContainer>
                  <LoadingSpinner />
                  <LoadingText>Generating your AI design...</LoadingText>
                </LoadingContainer>
              )}
              {generationError && !isGenerating && (
                <ErrorContainer>
                  <ErrorText>{generationError}</ErrorText>
                  <RetryButton onClick={handleRetry}>Try Again</RetryButton>
                </ErrorContainer>
              )}
              {resultImage && !isGenerating && !generationError && <ResultImage src={resultImage} alt="AI Generated Design" />}
            </ResultImageSection>

            <ResultBottomSection>
              <RegenerateTitle>Regenerate with other styles:</RegenerateTitle>

              <StylesPreviewRow>
                {designStyles
                  .filter(style => !style.isCustom)
                  .map(style => (
                    <StylePreviewCard key={style.id} selected={selectedRegenerateStyle === style.id} onClick={() => handleRegenerateStyleSelect(style.id)}>
                      <StylePreviewImage src={style.image} alt={style.name} />
                      <StylePreviewName selected={selectedRegenerateStyle === style.id}>{style.name}</StylePreviewName>
                    </StylePreviewCard>
                  ))}
              </StylesPreviewRow>

              <ResultButtonsSection>
                <RegenerateButton onClick={handleRegenerate} disabled={isGenerating}>
                  <RegenerateButtonText>AI Regenerate</RegenerateButtonText>
                  <CoinIcon>
                    <img src={coinIcon} alt="" />
                  </CoinIcon>
                  <RegenerateButtonText>1000</RegenerateButtonText>
                </RegenerateButton>

                <PostButton onClick={handlePost} disabled={isGenerating || !resultImage}>
                  Post
                </PostButton>
              </ResultButtonsSection>
            </ResultBottomSection>
          </ResultModalContainer>
        </ModalOverlay>
      )}

      {/* AI Post Filters Modal */}
      {showPostFilters && (
        <ModalOverlay onClick={handlePostFiltersClose}>
          <ResultModalContainer onClick={e => e.stopPropagation()}>
            <PostFilters onClose={handlePostFiltersClose} selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} isSending={isSending} onSubmitPost={handleSubmitAIPost} handleBack={handlePostFiltersBack} />
          </ResultModalContainer>
        </ModalOverlay>
      )}
    </AIDesignBody>
  )
}
